import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { KeyRound, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useAuth } from "@/contexts/AuthContext";
import { apiKeysApi, type ApiKey } from "@/lib/api";

// useApiKeyStatus — does this tenant have at least one *usable* API key?
// "Usable" = not revoked AND not past its expiry. The list endpoint already
// filters deleted rows server-side, so we only re-check revoked + expires_at
// in the client. Cached for 30 s so the banner + dialog don't double-fetch.
export function useApiKeyStatus(tenantCode: string) {
  const { idToken } = useAuth();
  const q = useQuery<ApiKey[]>({
    queryKey: ["api-keys", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      const res = await apiKeysApi.list(tenantCode, idToken);
      return res.api_keys;
    },
    enabled: !!idToken,
    staleTime: 30_000,
  });
  const now = Date.now();
  const hasActiveKey = (q.data ?? []).some((k) => {
    if (k.revoked_at) return false;
    if (k.expires_at && new Date(k.expires_at).getTime() <= now) return false;
    return true;
  });
  return {
    hasActiveKey,
    isLoading: q.isLoading,
    // While loading we don't want to flash the banner, so callers should
    // gate on `isResolved` before rendering.
    isResolved: !q.isLoading && !q.isError,
  };
}

// NoApiKeyBanner — top-of-page nudge that pulses to draw attention until the
// tenant mints their first API key. Renders nothing once a key exists. Always
// renders nothing while the status is still resolving (avoids a flash).
export function NoApiKeyBanner({ tenantCode }: { tenantCode: string }) {
  const { hasActiveKey, isResolved } = useApiKeyStatus(tenantCode);
  if (!isResolved || hasActiveKey) return null;
  return (
    <Link
      to="/api-keys/new"
      className={[
        "flex items-center gap-3 rounded-md border border-brand-violet/40",
        "bg-brand-violet/10 px-4 py-3 text-sm",
        "hover:bg-brand-violet/15 transition-colors",
        "animate-pulse focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      ].join(" ")}
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-brand-violet" />
      <span className="flex-1">
        <span className="font-semibold text-brand-violet">Setup API key</span>
        <span className="ml-2 text-muted-foreground">
          to start receiving tickets from your systems via the public API.
        </span>
      </span>
      <span className="shrink-0 text-xs text-brand-violet/80">
        Create one →
      </span>
    </Link>
  );
}

// NoApiKeyDialog — themed modal with Cancel + Setup API Key actions. Shown
// when the user attempts to create a ticket without a usable API key. The
// "Setup" button navigates to /api-keys/new (full route, so the user lands
// on the create form even if they refresh).
export function NoApiKeyDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>No API key found</DialogTitle>
              <DialogDescription className="mt-1">
                Tickets pushed in from your own systems are authenticated with
                a tenant-scoped API key. You haven't created one yet — set one
                up first, then come back to log this ticket.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button asChild>
            <Link to="/api-keys/new">Setup API Key</Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
